import axios, { type AxiosAdapter, type InternalAxiosRequestConfig } from 'axios';

export const axiosCorsHeaders = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Credentials': true,
  'Access-Control-Allow-Methods': 'GET,POST,PATCH,DELETE,OPTIONS',
  'Access-Control-Allow-Headers': 'Content-Type,Authorization'
};

const axiosInstance = axios.create({
  baseURL: '/',
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    ...axiosCorsHeaders
    // 'Authorization': `Bearer ${YOUR_ACCESS_TOKEN}`, // if we need to customize any auth tokens for URLS
  },
  withCredentials: true // Important for sending cookies/credentials across origins
});

const inFlightGetRequests = new Map<string, ReturnType<AxiosAdapter>>();

function getRequestKey(config: InternalAxiosRequestConfig) {
  if ((config.method ?? 'get').toUpperCase() !== 'GET') return null;

  const params = config.params
    ? Object.entries(config.params as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => `${key}=${String(value)}`)
        .join('&')
    : '';

  return `GET:${config.baseURL ?? ''}${config.url ?? ''}?${params}`;
}

const defaultAdapter = axios.getAdapter(axiosInstance.defaults.adapter);

const deduplicatingAdapter: AxiosAdapter = (config) => {
  // Cypress intercepts each test request independently. Do not share an
  // in-flight promise between tests, otherwise a previous test can consume
  // the request before the current test's alias observes it.
  if (typeof window !== 'undefined' && window.Cypress) {
    return defaultAdapter(config);
  }

  // A caller with an AbortSignal owns its request lifecycle. Sharing that
  // promise would let one caller's cancellation reject another caller's GET.
  if (config.signal) return defaultAdapter(config);

  const key = getRequestKey(config);

  if (!key) return defaultAdapter(config);

  const existingRequest = inFlightGetRequests.get(key);
  if (existingRequest) return existingRequest;

  const request = defaultAdapter(config).finally(() => {
    inFlightGetRequests.delete(key);
  });

  inFlightGetRequests.set(key, request);
  return request;
};

axiosInstance.defaults.adapter = deduplicatingAdapter;

let isRedirectingToAuth = false;

const shouldSkipAuthRedirect = (url?: string) => {
  if (!url) return false;
  return (
    url.includes('/api/user/sign-out') ||
    url.includes('/api/oauth/sign-in') ||
    url.includes('/api/oauth/callback') ||
    url.includes('/api/oauth/refresh') ||
    url.includes('/api/auth/guardian/passwordless/verify-otp')
  );
};

// Optional: Add request or response interceptors for global error handling, logging, etc.
axiosInstance.interceptors.request.use(
  (config) => {
    // Modify request config before sending
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // Handle successful responses
    return response;
  },
  (error) => {
    // Redirect to auth flow when the API indicates the session is no longer valid.
    if (typeof window !== 'undefined') {
      const status = error?.response?.status;
      const url = error?.config?.url as string | undefined;
      const currentPath = window.location.pathname;
      const isAuthPage =
        currentPath.startsWith('/sign-in') ||
        currentPath.startsWith('/guardian/sign-in') ||
        currentPath.startsWith('/logout');

      if (status === 401 && !isRedirectingToAuth && !isAuthPage && !shouldSkipAuthRedirect(url)) {
        isRedirectingToAuth = true;
        window.location.assign(currentPath.startsWith('/guardian') ? '/logout?portal=guardian' : '/logout');
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
